import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import ReplyTemplateTable from '../../../components/tables/ReplyTemplateTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useReplyTemplateApi } from '../../../hooks/useReplyTemplateApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import {
  ComponentActionDTO,
  ReplyTemplateDTO,
} from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';
import { useComponentActionApi } from '../../../hooks/useComponentActionApi';

const ReplyTemplateList: React.FC = () => {
  const { search, remove, isLoading, error } = useReplyTemplateApi();
  const [items, setItems] = useState<ReplyTemplateDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchFilters, setSearchFilters] = useState<any>({});
  const [components, setComponents] = useState<ComponentActionDTO[]>([]);
  const { search: searchComponents } = useComponentActionApi();

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Reply Templates', href: '#' },
  ];

  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      const response = await search({
        filters,
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });
      setItems(response?.data?.results || []);
      setTotalRecords(response?.data?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching Reply Templates:', error);
    }
  };

  // Add after existing useEffect
  useEffect(() => {
    const fetchComponents = async () => {
      const response = await searchComponents({
        page: 1,
        pageSize: 9999,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });

      if (response?.success && response.data) {
        setComponents(response.data.results ?? []);
      }
    };

    fetchComponents();
  }, []);

  useEffect(() => {
    fetchItems(currentPage, pageSize, {});
  }, [currentPage, pageSize]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
  };

  const handleClearSearch = () => {
    setSearchFilters({});
    fetchItems(1, pageSize, {});
  };

  const handleDelete = async (dto: ReplyTemplateDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await fetchItems(currentPage, pageSize, {});
    } catch (error) {
      console.error('Error deleting ReplyTemplate:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/replytemplates"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Settings.ReplyTemplate.View"
    >
      <Card
        title="Manage Reply Templates"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <AddNewButton
            onClick={() => navigate('/admin/replytemplates/create')}
          />
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormField
              id="title"
              label="Search by Title"
              value={searchFilters.title || ''}
              onChange={(e) =>
                setSearchFilters({ ...searchFilters, title: e.target.value })
              }
              placeholder="Search by Title"
            />
            <FormField
              id="componentActionId"
              label="Search by Component"
              type="dropdown"
              value={searchFilters.componentActionId || ''}
              onChange={(e) =>
                setSearchFilters({
                  ...searchFilters,
                  componentActionId: e.target.value,
                })
              }
              placeholder="All Components"
              options={[
                ...components.map((comp) => ({
                  value: comp.id,
                  label: comp.title,
                })),
              ]}
            />
          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <ReplyTemplateTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/replytemplates/${dto.id}`)}
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default ReplyTemplateList;
